// ensureEzAuth loads (or reuses) the ezAuth Visitor Accounts SDK for
// auth_mode=ezauth domains and reads the logged-in visitor's access token. SM
// resolves that token to an email server-side via PUA, so the plaintext email
// never reaches the page. Any failure (domain conflict, script load failure,
// init timeout, token read error) logs a setup error and returns null, so the
// domain falls back to the guest flow rather than breaking checkout.

import { logError } from '@/sdkConsole';

// Canonical sanctioned ezAuth embed (same bundle the drop-in publisher install
// loads). The bare URL serves the current Visitor Accounts SDK bundle.
const EZAUTH_SCRIPT_URL = 'https://www.ezojs.com/detroitchicago/ferndale.js';

// Upper bound on waiting for an injected bundle to publish + initialize its
// window.ezAuth facade before giving up and treating the visitor as a guest.
const EZAUTH_INIT_TIMEOUT_MS = 10000;
const EZAUTH_POLL_INTERVAL_MS = 50;

// Query params sol appends to its emailed account links (password reset,
// email verification). Mirror RESET_TOKEN_PARAM / VERIFY_TOKEN_PARAM in
// staticsol/node_js/ezauth/src/config.ts — ezAuth is a separately-deployed
// bundle, so SM mirrors the names rather than importing them. The ezAuth form
// reads the same params at mount to pick its reset/verify screen.
export const EZAUTH_RESET_TOKEN_PARAM = 'ezauth_reset_token';
export const EZAUTH_VERIFY_TOKEN_PARAM = 'ezauth_verify_token';

export type EzAuthUrlTokenKind = 'reset' | 'verify';

// ezAuthUrlTokenKind reports which ezAuth account-link token the current URL
// carries, or null. Reset wins when both are present (matches ezAuth form.ts).
// Empty values do not count. Pure when given an explicit search string.
export function ezAuthUrlTokenKind(search?: string): EzAuthUrlTokenKind | null {
  const raw = typeof search === 'string' ? search : (typeof window !== 'undefined' ? window.location.search : '');
  const qs = raw.charAt(0) === '?' ? raw.slice(1) : raw;
  if (qs === '') {
    return null;
  }
  const params = new URLSearchParams(qs);
  if (params.get(EZAUTH_RESET_TOKEN_PARAM)) {
    return 'reset';
  }
  if (params.get(EZAUTH_VERIFY_TOKEN_PARAM)) {
    return 'verify';
  }
  return null;
}

// stripEzAuthUrlToken removes both ezAuth account-link token params from the
// current page URL (history.replaceState; no navigation, other params and the
// hash preserved). Called when the token-landing overlay is dismissed — "not
// now" — so the overlay does not reappear on every reload of the emailed link.
// The token is unspent and still usable by re-clicking the email. No-op when
// neither param is present.
export function stripEzAuthUrlToken(): void {
  if (typeof window === 'undefined' || !window.location || !window.history || typeof window.history.replaceState !== 'function') {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  if (!params.has(EZAUTH_RESET_TOKEN_PARAM) && !params.has(EZAUTH_VERIFY_TOKEN_PARAM)) {
    return;
  }
  params.delete(EZAUTH_RESET_TOKEN_PARAM);
  params.delete(EZAUTH_VERIFY_TOKEN_PARAM);
  const remaining = params.toString();
  window.history.replaceState(null, '', window.location.pathname + (remaining ? '?' + remaining : '') + window.location.hash);
}

// ezAuth auth-state, as delivered to an onChange listener. SM only inspects the
// status; an 'authenticated' status means a token is now readable.
export interface EzAuthAuthState {
  status: 'anonymous' | 'authenticated' | string;
}

// Options accepted by the ezAuth drop-in form (window.ezAuth.mount).
export type EzAuthFormScreen =
  | 'login'
  | 'register'
  | 'forgot'
  | 'reset'
  | 'verify'
  | 'signed-in'
  | 'notice';

export interface EzAuthMountOptions {
  mode?: 'login' | 'register';
  showToggle?: boolean;
  // Fires on every screen transition (including the initial paint). Hosts that
  // hide ezAuth's heading and render their own chrome use this instead of
  // scraping the ezauth--* root class.
  onScreenChange?: (screen: EzAuthFormScreen) => void;
  // Whether the form renders its own Facebook/Apple/Microsoft buttons (SDK
  // default true). SM passes false: it renders those buttons itself.
  socialProviders?: boolean;
}

// Handle returned by mount(); unmount() removes the form. Idempotent.
export interface EzAuthFormHandle {
  unmount: () => void;
}

// Popup-based account login providers (Google has its own GIS transport).
export type SocialProvider = 'facebook' | 'apple' | 'microsoft';

export const SOCIAL_PROVIDERS: readonly SocialProvider[] = ['facebook', 'apple', 'microsoft'];

// Which OAuth login providers the domain has configured, as returned by the
// facade's getOAuthConfig(). clientId is the PUBLIC Google client ID handed to
// Google Identity Services; it is present only when google.enabled is true.
// The social keys are optional: an older ezAuth bundle omits them, which
// means disabled (see socialProviderEnabled).
export type EzAuthOAuthConfig = { google: { enabled: boolean; clientId?: string } } & Partial<Record<SocialProvider, { enabled: boolean }>>;

export function socialProviderEnabled(config: EzAuthOAuthConfig, provider: SocialProvider): boolean {
  return config[provider]?.enabled === true;
}

const inFlightOAuthConfig = new WeakMap<EzAuthFacade, Promise<EzAuthOAuthConfig>>();

// sharedOAuthConfig joins concurrent getOAuthConfig() calls on one facade so the
// Google and social buttons, mounted together, make a single
// /account/oauth/config request. Only in-flight calls are shared; a later mount
// fetches fresh, as before.
export function sharedOAuthConfig(facade: EzAuthFacade): Promise<EzAuthOAuthConfig> {
  const pending = inFlightOAuthConfig.get(facade);
  if (pending) {
    return pending;
  }
  const request = facade.getOAuthConfig().finally(() => {
    inFlightOAuthConfig.delete(facade);
  });
  inFlightOAuthConfig.set(facade, request);
  return request;
}

// The slice of the ezAuth (staticsol) facade SM depends on. Mirrored locally
// rather than imported: ezAuth is a separately-deployed bundle, not a build-time
// dependency, so SM relies only on this narrow, blessed contract.
export interface EzAuthFacade {
  isInitialized: () => boolean;
  getDomainId: () => number | null;
  getAccessToken: () => Promise<string | null>;
  init: (config: { domainId: number }) => void;
  // Subscribe to auth-state changes; fires immediately with the current state.
  // Returns an unsubscribe function. Used by the identity gate to detect a
  // successful in-page login/register.
  onChange: (listener: (state: EzAuthAuthState) => void) => () => void;
  // Render the drop-in login/register form into a target element.
  mount: (target: string | HTMLElement, options?: EzAuthMountOptions) => EzAuthFormHandle;
  // Discover which OAuth login providers are configured for this domain, so the
  // caller renders only the buttons that will work (e.g. a Google button).
  getOAuthConfig: () => Promise<EzAuthOAuthConfig>;
  // Obtain a single-use, server-issued nonce to pass to Google Identity Services
  // before requesting an id_token; the returned id_token echoes it, proving to
  // the server that the token belongs to a flow it started.
  getOAuthNonce: () => Promise<string>;
  // Complete a Google login from a Google Identity Services id_token, adopting
  // the issued session exactly like an email/password login.
  loginWithGoogleCredential: (idToken: string) => Promise<unknown>;
  // Complete a Facebook/Apple/Microsoft login through an ezAuth-owned popup,
  // adopting the issued session. Must be called synchronously inside the
  // click (the popup opens before any await). Absent on older bundles, so
  // callers check typeof === 'function'. Rejects with EzAuthError codes
  // (cancelled, popup_blocked, email_required, account_exists, ...).
  loginWithSocial?: (provider: SocialProvider) => Promise<unknown>;
  // End the visitor's ezAuth session on this device: clears ezAuth's local
  // session and revokes it server-side. Idempotent (safe when already logged
  // out). SM calls this from ezsubscriptions.logout() so a logged-out ezAuth
  // visitor is not silently re-established from their token on the next load.
  logout: () => Promise<void>;
  // Register from a PUA-minted guest-checkout registration token instead of a
  // plaintext email — the guest→account conversion path, where the visitor
  // sets only a password and sol sources the email from the attestation. The
  // SDK owns the register call and adopts the issued session. Rejects with
  // EzAuthError codes (account_exists, password_compromised, invalid_request).
  // options.skipVerificationEmail suppresses the post-register verification
  // email (sol honors it only on this token path). SM deliberately does not
  // use it: the trust gate refuses an unverified account on a domain where
  // the email has data, and a converted guest always has data on the domain
  // they just bought on — the verification email is what lifts that.
  registerWithEmailToken: (emailToken: string, password: string, options?: { skipVerificationEmail?: boolean }) => Promise<unknown>;
}

interface EzAuthWindow extends Window {
  ezAuth?: EzAuthFacade;
  ezAuthConfig?: { domainId?: number; [key: string]: unknown };
}

// ensureEzAuth resolves to the visitor's ezAuth access token, or null when ezAuth
// is unavailable for this domain (anonymous visitor, conflict, or setup failure).
export async function ensureEzAuth(domainId: number): Promise<string | null> {
  const facade = await ensureEzAuthFacade(domainId);
  if (!facade) {
    return null;
  }
  return readAccessToken(facade);
}

// ensureEzAuthFacade loads (or reuses) the ezAuth SDK and returns its initialized
// facade, or null when ezAuth is unavailable for this domain (conflict or setup
// failure). The identity gate uses the facade's mount()/onChange() to host the
// in-page login/register form; ensureEzAuth() uses it to read the access token.
export async function ensureEzAuthFacade(domainId: number): Promise<EzAuthFacade | null> {
  const win = window as EzAuthWindow;

  const conflict = conflictingDomain(win, domainId);
  if (conflict !== null) {
    logError(`ezAuth is already configured for domain ${conflict}, not ${domainId}; ezAuth identity is disabled here`);
    return null;
  }

  // Reuse an already-initialized facade (publisher installed Visitor Accounts).
  if (win.ezAuth?.isInitialized()) {
    return win.ezAuth;
  }

  win.ezAuthConfig = { ...win.ezAuthConfig, domainId };

  // A facade present but not yet initialized (bundle loaded without config) is
  // initialized directly; otherwise inject the bundle, which publishes the
  // facade and auto-initializes + restores from window.ezAuthConfig.
  if (win.ezAuth) {
    try {
      win.ezAuth.init({ domainId });
    } catch (err) {
      logError('ezAuth.init failed; ezAuth identity is disabled here', err);
      return null;
    }
  } else {
    try {
      await injectScript(EZAUTH_SCRIPT_URL);
    } catch (err) {
      logError('failed to load the ezAuth script; ezAuth identity is disabled here', err);
      return null;
    }
  }

  const facade = await waitForInitializedFacade(win);
  if (!facade) {
    logError('ezAuth did not initialize in time; ezAuth identity is disabled here');
    return null;
  }
  return facade;
}

// conflictingDomain returns the domain ezAuth is already pinned to when it
// differs from this domain (initialized facade or publisher-set config), else
// null. We must not silently re-point an existing install at a different account.
function conflictingDomain(win: EzAuthWindow, domainId: number): number | null {
  if (win.ezAuth?.isInitialized()) {
    const installed = win.ezAuth.getDomainId();
    if (installed !== null && installed !== domainId) {
      return installed;
    }
  }
  const configured = win.ezAuthConfig?.domainId;
  if (typeof configured === 'number' && configured !== domainId) {
    return configured;
  }
  return null;
}

// signOutEzAuth ends the visitor's ezAuth session for an ezauth-mode domain. It
// loads (or reuses) the facade first, so a returning member — whose page load
// skipped ezAuth init because an SM session already existed — is actually signed
// out and the stored refresh token (ezauth.session.<domainId>) cannot
// re-establish the SM session on the next load. No-op when ezAuth is unavailable
// for the domain (conflict or setup failure). Best-effort revoke: ezAuth.logout()
// clears its local session before the server revoke, so a failed revoke still
// leaves the visitor logged out locally.
export async function signOutEzAuth(domainId: number): Promise<void> {
  const facade = await ensureEzAuthFacade(domainId);
  if (!facade) {
    return;
  }
  try {
    await facade.logout();
  } catch (err) {
    logError('ezAuth logout failed', err);
  }
}

async function readAccessToken(facade: EzAuthFacade): Promise<string | null> {
  try {
    const token = await facade.getAccessToken();
    return typeof token === 'string' && token !== '' ? token : null;
  } catch (err) {
    logError('ezAuth.getAccessToken failed', err);
    return null;
  }
}

// injectScript appends a single async <script> and resolves on load / rejects on
// error. A matching tag already in the DOM (double bootstrap) is reused.
function injectScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('ezAuth script load failed')), { once: true });
    document.head.appendChild(script);
  });
}

// waitForInitializedFacade polls until window.ezAuth reports it is initialized,
// or the timeout elapses (resolves null).
function waitForInitializedFacade(win: EzAuthWindow): Promise<EzAuthFacade | null> {
  return new Promise((resolve) => {
    const deadline = Date.now() + EZAUTH_INIT_TIMEOUT_MS;
    const check = (): void => {
      if (win.ezAuth?.isInitialized()) {
        resolve(win.ezAuth);
        return;
      }
      if (Date.now() >= deadline) {
        resolve(null);
        return;
      }
      setTimeout(check, EZAUTH_POLL_INTERVAL_MS);
    };
    check();
  });
}
